import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";

import {
  type ContestFormDto,
  createContestDtoSchema,
  type CompanyResponseDto,
} from "slde-api/dto-schemas";
import { DateFormField } from "@/components/date.form-field";

interface ContestFormProps {
  defaultValues?: ContestFormDto;
  companies: Pick<CompanyResponseDto, "id" | "title">[];
  onSubmit: (values: ContestFormDto) => void;
  disabled?: boolean;
  loading?: boolean;
}

export function ContestForm({
  onSubmit: onSubmitProps,
  defaultValues,
  companies,
  disabled,
  loading,
}: ContestFormProps) {
  const form = useForm<ContestFormDto>({
    resolver: zodResolver(createContestDtoSchema),
    defaultValues: defaultValues ?? {},
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmitProps)}
        className="space-y-4 w-full"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field: { value, ...field } }) => (
            <FormItem>
              <FormLabel>Nazwa</FormLabel>
              <FormControl>
                <Input value={value ?? ""} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field: { value, ...field } }) => (
            <FormItem>
              <FormLabel>Opis</FormLabel>
              <FormControl>
                <Textarea value={value ?? ""} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="dueDate"
          render={({ field: { value, ...field } }) => (
            <FormItem>
              <FormLabel>Termin</FormLabel>
              <FormControl>
                <Input value={value ?? ""} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DateFormField control={form.control} name="dueDate" label="Termin" />

        <FormField
          control={form.control}
          name="linkUrl"
          render={({ field: { value, ...field } }) => (
            <FormItem>
              <FormLabel>Link</FormLabel>
              <FormControl>
                <Input value={value ?? ""} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="companyId"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormLabel>Wystawca</FormLabel>
              <FormControl>
                <Combobox
                  items={companies}
                  value={value}
                  onValueChange={onChange}
                  autoHighlight
                  itemToStringLabel={(id) =>
                    companies.find((c) => c.id === id)?.title ?? ""
                  }
                >
                  <ComboboxInput showClear className="w-full" />
                  <ComboboxContent>
                    <ComboboxEmpty>Brak wyników</ComboboxEmpty>
                    <ComboboxList>
                      {(company: Pick<CompanyResponseDto, "id" | "title">) => (
                        <ComboboxItem key={company.id} value={company.id}>
                          {company.title}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={disabled || loading}>
          {!!defaultValues ? "Edytuj" : "Dodaj"}
          {loading && <Loader2 className="animate-spin" />}
        </Button>
      </form>
    </Form>
  );
}
